import React, { useState, useCallback, useEffect } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import dateHandler from "@/composables/dateUtil";
import { toast } from "react-toastify";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";
import FilterModal from "@/components/common/FilterModal";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import { useNavigate } from "react-router-dom";
import ApproveIcon from "@styles/all-images/icons/checkblue.svg";
import RejectIcon from "@styles/all-images/icons/closeblue.svg";
import TierConfigurationRejectionReason from "@/pages/TierConfiguration/TierConfigurationRejectionReason";
import { IMTSDocumentApprovalStatus } from "@/constants/enums/im-ts-document-status.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";

interface ReceivedTermSheetTableProps {
  tabId: number;
}

const ReceivedTermSheetTable: React.FC<ReceivedTermSheetTableProps> = ({
  tabId,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const navigate = useNavigate();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [docRejectingID, setDocRejectingID] = useState(null);
  const { isPermitted } = usePermissionState();

  useEffect(() => {
    if (selectedCompanyId !== undefined) {
      setForceRefresh((prev) => !prev);
    }
  }, [selectedCompanyId]);

  const handleView = useCallback((doc: any) => {
    navigate(`/view-im-ts-document/${doc.id}?type=ts&history=false`);
  }, []);

  const handleApprove = useCallback(
    async (doc: any) => {
      try {
        navigate(`/view-im-ts-document/${doc.id}?type=ts&history=false`);
      } catch (error) {
        console.error("Error approving document:", error);
      }
    },
    [dispatch, navigate],
  );

  const toggleRejectModal = (doc?: any) => {
    setIsRejectModalOpen(!isRejectModalOpen);
    if (doc) {
      setDocRejectingID(doc.id);
    }
  };

  const handleReject = useCallback(
    async (note: string) => {
      if (!docRejectingID) {
        return;
      }
      setIsLoading(true);
      try {
        const { authenticatedCall } = actionsHandler(
          "approveRejectIMTSDocument",
        );
        const payload = {
          status: IMTSDocumentApprovalStatus.Rejected,
          note,
        };
        const response = await dispatch(
          authenticatedCall({
            method: "put",
            customParams: [docRejectingID],
            payload,
          }),
        );
        toast.success(response.data.message);
        setForceRefresh(true);
        toggleRejectModal();
        setDocRejectingID(null);
      } catch (error) {
        console.error("Error rejecting document:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, docRejectingID],
  );

  const columns = [
    {
      header: "Document Name",
      accessor: "document_name",
      sortable: true,
      sortKey: "document_name",
    },
    {
      header: "Super Trader(T4)",
      accessor: "super_trader",
      sortable: true,
      sortKey: "super_trader",
    },
    {
      header: "Uploaded on Date & Time",
      accessor: (row: any) => formatDate(row.uploaded_on),
      sortable: true,
      sortKey: "uploaded_on",
    },
    {
      header: "Received on Date & Time",
      accessor: (row: any) => formatDate(row.received_on),
      sortable: true,
      sortKey: "received_on",
    },
    {
      header: "Sent By",
      accessor: "sent_by",
      sortable: true,
      sortKey: "sent_by",
    },
    {
      header: "Status",
      accessor: (row: any) => (
        <StatusChips
          status={row.approval_status}
          type={StatusChipEnumType.IMTSDocumentApproval}
          showRejectionNote={true}
          rejectionNote={row.note}
        />
      ),
      sortable: true,
      sortKey: "approval_status",
    },
    {
      header: "Actions",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
          {row.approval_status === IMTSDocumentApprovalStatus.Pending &&
            isPermitted(DocumentsPermissions.APPROVE_REJECT_IM_DOCUMENTS) && (
              <>
                <button onClick={() => handleApprove(row)} title="Approve">
                  <img
                    src={ApproveIcon}
                    alt="Approve"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
                <button onClick={() => toggleRejectModal(row)} title="Reject">
                  <img
                    src={RejectIcon}
                    alt="Reject"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              </>
            )}
        </div>
      ),
    },
  ];

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler("receivedTermSheetList");

        const queryParams = new URLSearchParams({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
          ...(selectedCompanyId && {
            company_id: selectedCompanyId,
          }),
        }).toString();

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: queryParams,
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching received term sheets:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1 },
          },
        };
      }
    },
    [dispatch, selectedCompanyId],
  );

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        filterButton={{
          text: "Filter",
          icon: FilterIcon,
          onClick: () => setIsFilterModalOpen(true),
        }}
        noDataMessage="No term sheet documents found"
        noSearchDataMessage="No matching term sheet documents found"
      />

      {isRejectModalOpen && (
        <TierConfigurationRejectionReason
          onClose={toggleRejectModal}
          onSubmit={handleReject}
          isLoading={isLoading}
        />
      )}

      {isFilterModalOpen && (
        <FilterModal
          onClose={() => setIsFilterModalOpen(false)}
          onApply={(companyId) => {
            setSelectedCompanyId(companyId || 0);
            setIsFilterModalOpen(false);
          }}
          isOpen={isFilterModalOpen}
          currentFilter={selectedCompanyId || 0}
        />
      )}
    </>
  );
};

export default ReceivedTermSheetTable;
